import { Injectable } from '@angular/core';
import { AuthService } from '@core/modules/authentication/services/auth.service';
import {
  isNewClient,
  loadProfessions,
  loadUserFulfilled,
  selectUser,
  updateUserFulfilled
} from '@core/modules/user/features/user.slice';
import { TokenStorageService } from '@core/services/token-storage.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { isEmpty } from 'lodash';
import { of } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';
import {
  languageChanged,
  languageChangedFulfilled,
  languageChangedRejected,
  loadLanguages,
  loadLanguagesFulfilled,
  loadLanguagesRejected,
  selectCurrentLanguage,
  selectLocales,
  updateTokenLanguageFulfilled,
  updateTokenLanguageRejected
} from '../features/language.slice';
import { LanguageService } from '../services/language.service';
import { UserRole } from '@core/models';

@Injectable()
export class LanguageEffects {
  loadLanguages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadLanguages),
      switchMap(() =>
        this.languageService.getLanguages().pipe(
          map((languages) => loadLanguagesFulfilled(languages)),

          catchError(() => of(loadLanguagesRejected()))
        )
      )
    )
  );

  userLoaded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadUserFulfilled),
      map((action) =>
        languageChanged({ langId: action.payload.langId, countryId: action.payload.countryId })
      )
    )
  );

  userUpdate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateUserFulfilled),
      concatLatestFrom(() => this.store.select(selectCurrentLanguage)),
      map(([action, language]) =>
        action.payload.langId !== language?.id
          ? languageChanged({ langId: action.payload.langId, countryId: action.payload.countryId })
          : languageChangedFulfilled({
              langId: action.payload.langId,
              countryId: action.payload.countryId
            })
      )
    )
  );

  updateTokenLanguage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(languageChanged),
      concatLatestFrom(() => [this.store.select(isNewClient), this.store.select(selectUser)]),
      switchMap(([action, isNewClient, user]) =>
        (!user
          ? of('')
          : this.authService.updateTokenLanguage(action.payload).pipe(
              tap((token) => this.tokenStorageService.saveToken(token)),
              tap(() => {
                // UPDATING If language/token changes then we do update professions list
                if (
                  !isNewClient &&
                  (user?.roleId === UserRole.CLIENT || user?.roleId === UserRole.ADMIN)
                ) {
                  this.store.dispatch(loadProfessions());
                }
              })
            )
        ).pipe(
          map(() => updateTokenLanguageFulfilled(action.payload)),
          catchError(() => of(updateTokenLanguageRejected()))
        )
      )
    )
  );

  languageChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateTokenLanguageFulfilled),
      concatLatestFrom(() =>
        this.store.select(selectLocales).pipe(filter((locales) => !isEmpty(locales)))
      ),
      switchMap(([action, locales]) =>
        this.languageService
          .useLanguage(locales!.find((locale) => locale.id === action.payload.langId)?.locale)
          .pipe(
            map(() => languageChangedFulfilled(action.payload)),
            catchError(() => of(languageChangedRejected()))
          )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private store: Store,
    private languageService: LanguageService,
    private authService: AuthService,
    private tokenStorageService: TokenStorageService
  ) {}
}
